"use client";

import { useEffect, useState } from "react";
import { Eye, EyeOff, Lock, Mail, ShieldCheck, LoaderCircle, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";

/**
 * Вход в СРЕДА: настоящий email + пароль против backend через BFF-прокси.
 * Двухшаговый flow в одной странице:
 *   step="login"  — email + пароль (CSRF round-trip).
 *   step="change" — принудительная смена пароля при must_change.
 * Брендовая страница (графит + изумруд).
 */

type Step = "login" | "change";

/** Берём свежий CSRF-токен; cookie sreda_csrf ставится сервером в этом же ответе. */
async function fetchCsrf(): Promise<string> {
  const res = await fetch("/api/auth/csrf", { credentials: "same-origin" });
  if (!res.ok) throw new Error("csrf");
  const data = (await res.json()) as { csrf?: string };
  if (!data?.csrf) throw new Error("csrf");
  return data.csrf;
}

export default function LoginPage() {
  const [step, setStep] = useState<Step>("login");

  // login step
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);

  // change step
  const [newpw, setNewpw] = useState("");
  const [repeat, setRepeat] = useState("");
  const [revealNew, setRevealNew] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // SSO кнопка показывается только если backend подтвердил что OIDC включён.
  const [oidcEnabled, setOidcEnabled] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch("/api/auth/oidc/status", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((d: { enabled?: boolean }) => { if (alive) setOidcEnabled(!!d?.enabled); })
      .catch(() => { /* offline / 404 — кнопку не показываем */ });
    return () => { alive = false; };
  }, []);

  async function submitLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password || loading) return;
    setLoading(true);
    setError("");
    try {
      const csrf = await fetchCsrf();
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF": csrf },
        body: JSON.stringify({ email, password }),
        credentials: "same-origin",
      });
      if (res.ok) {
        const data = (await res.json().catch(() => ({}))) as {
          user?: { must_change?: boolean };
        };
        if (data?.user?.must_change) {
          setError("");
          setStep("change");
          return;
        }
        // Фиксированный внутренний путь — никакого user-controlled redirect.
        // На потребительском хосте нет /dashboard (это портал) — идём на «/», middleware отдаст чат /u.
        window.location.assign(["user.sreda-ai.ru", "dev-user.sreda-ai.ru"].includes(location.hostname) ? "/" : "/dashboard");
        return;
      }
      if (res.status === 401) {
        setError("Неверный email или пароль");
      } else if (res.status === 429) {
        setError("Слишком много попыток, подождите минуту");
      } else {
        setError("Не удалось войти, попробуйте ещё раз");
      }
    } catch {
      setError("Сеть недоступна, попробуйте ещё раз");
    } finally {
      setLoading(false);
    }
  }

  async function submitChange(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;
    if (newpw.length < 8) {
      setError("Пароль должен быть не короче 8 символов");
      return;
    }
    if (newpw !== repeat) {
      setError("Пароли не совпадают");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const csrf = await fetchCsrf();
      const res = await fetch("/api/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF": csrf },
        body: JSON.stringify({ new_password: newpw }),
        credentials: "same-origin",
      });
      if (res.ok) {
        // На потребительском хосте нет /dashboard (это портал) — идём на «/», middleware отдаст чат /u.
        window.location.assign(["user.sreda-ai.ru", "dev-user.sreda-ai.ru"].includes(location.hostname) ? "/" : "/dashboard");
        return;
      }
      if (res.status === 429) {
        setError("Слишком много попыток, подождите минуту");
      } else {
        setError("Не удалось сменить пароль, попробуйте ещё раз");
      }
    } catch {
      setError("Сеть недоступна, попробуйте ещё раз");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="flex min-h-full items-center justify-center px-4 py-16">
      <div className="w-full max-w-[380px]">
        <div className="mb-7 flex flex-col items-center text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-acc/12 text-acc">
            <ShieldCheck size={24} strokeWidth={2} />
          </div>
          <h1 className="text-[22px] font-semibold tracking-tight text-txt">СРЕДА</h1>
          <p className="mt-1.5 text-[13px] leading-relaxed text-muted">
            {step === "login" ? (
              <>
                Операционная среда компании на ИИ.
                <br />
                Войдите по корпоративной почте.
              </>
            ) : (
              <>
                Первый вход.
                <br />
                Задайте новый пароль для аккаунта.
              </>
            )}
          </p>
        </div>

        {step === "login" ? (
          <form
            onSubmit={submitLogin}
            className="rounded-md border border-line bg-panel p-5 shadow-[var(--shadow-card-md)]"
          >
            <label htmlFor="login-email" className="mb-1.5 block text-[12px] font-medium text-txt2">
              Email
            </label>
            <div className="relative">
              <Mail
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint"
              />
              <input
                id="login-email"
                type="email"
                autoFocus
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={cn(
                  "w-full rounded-xs border bg-bg2 py-2.5 pl-9 pr-3 text-[14px] text-txt outline-none transition",
                  "placeholder:text-faint focus:border-acc/60 focus:bg-bg3",
                  error ? "border-red/50" : "border-line",
                )}
                placeholder="you@company.ru"
              />
            </div>

            <label
              htmlFor="login-pass"
              className="mb-1.5 mt-4 block text-[12px] font-medium text-txt2"
            >
              Пароль
            </label>
            <div className="relative">
              <Lock
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint"
              />
              <input
                id="login-pass"
                type={reveal ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={cn(
                  "w-full rounded-xs border bg-bg2 py-2.5 pl-9 pr-10 text-[14px] text-txt outline-none transition",
                  "placeholder:text-faint focus:border-acc/60 focus:bg-bg3",
                  error ? "border-red/50" : "border-line",
                )}
                placeholder="введите пароль"
              />
              <button
                type="button"
                onClick={() => setReveal((v) => !v)}
                aria-label={reveal ? "Скрыть пароль" : "Показать пароль"}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-faint transition hover:text-txt2"
              >
                {reveal ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            {error && <p className="mt-2 text-[12px] text-red">{error}</p>}

            <Button
              type="submit"
              variant="go"
              disabled={loading || !email || !password}
              className="mt-4 w-full"
            >
              {loading ? <LoaderCircle size={15} className="animate-spin" /> : null}
              {loading ? "Проверяю…" : "Войти"}
            </Button>

            {oidcEnabled && (
              <>
                <div className="my-4 flex items-center gap-3 text-[11px] text-faint">
                  <div className="h-px flex-1 bg-line" />
                  <span>или</span>
                  <div className="h-px flex-1 bg-line" />
                </div>
                <a
                  href="/api/auth/oidc/login"
                  className={cn(
                    "flex w-full items-center justify-center gap-2 rounded-xs border border-line bg-bg2 py-2.5 text-[14px] text-txt",
                    "transition hover:border-acc/60 hover:bg-bg3",
                  )}
                >
                  <KeyRound size={15} />
                  Войти через Keycloak
                </a>
              </>
            )}
          </form>
        ) : (
          <form
            onSubmit={submitChange}
            className="rounded-md border border-line bg-panel p-5 shadow-[var(--shadow-card-md)]"
          >
            <h2 className="mb-3 text-[15px] font-semibold text-txt">Задайте новый пароль</h2>

            <label
              htmlFor="change-new"
              className="mb-1.5 block text-[12px] font-medium text-txt2"
            >
              Новый пароль
            </label>
            <div className="relative">
              <Lock
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint"
              />
              <input
                id="change-new"
                type={revealNew ? "text" : "password"}
                autoFocus
                autoComplete="new-password"
                value={newpw}
                onChange={(e) => setNewpw(e.target.value)}
                className={cn(
                  "w-full rounded-xs border bg-bg2 py-2.5 pl-9 pr-10 text-[14px] text-txt outline-none transition",
                  "placeholder:text-faint focus:border-acc/60 focus:bg-bg3",
                  error ? "border-red/50" : "border-line",
                )}
                placeholder="минимум 8 символов"
              />
              <button
                type="button"
                onClick={() => setRevealNew((v) => !v)}
                aria-label={revealNew ? "Скрыть пароль" : "Показать пароль"}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-faint transition hover:text-txt2"
              >
                {revealNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <label
              htmlFor="change-repeat"
              className="mb-1.5 mt-4 block text-[12px] font-medium text-txt2"
            >
              Повторите пароль
            </label>
            <div className="relative">
              <Lock
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint"
              />
              <input
                id="change-repeat"
                type={revealNew ? "text" : "password"}
                autoComplete="new-password"
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                className={cn(
                  "w-full rounded-xs border bg-bg2 py-2.5 pl-9 pr-3 text-[14px] text-txt outline-none transition",
                  "placeholder:text-faint focus:border-acc/60 focus:bg-bg3",
                  error ? "border-red/50" : "border-line",
                )}
                placeholder="ещё раз"
              />
            </div>

            {error && <p className="mt-2 text-[12px] text-red">{error}</p>}

            <Button
              type="submit"
              variant="go"
              disabled={loading || !newpw || !repeat}
              className="mt-4 w-full"
            >
              {loading ? <LoaderCircle size={15} className="animate-spin" /> : null}
              {loading ? "Сохраняю…" : "Сохранить и войти"}
            </Button>
          </form>
        )}

        <p className="mt-5 text-center text-[11px] text-faint">
          Доступ по корпоративному аккаунту. Вопросы — администратору среды.
        </p>
      </div>
    </main>
  );
}
